import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './CoffeeToolsPage.module.css';
import coffeeCostGuide from '../../util/coffeeCostGuide';

const tools = [
  {
    slug: 'coffee-taste-fixer',
    title: 'Coffee Taste Fixer',
    description: 'Fix coffee that tastes bitter, sour, weak, harsh, or dull.',
  },
  {
    slug: 'coffee-ratio-calculator',
    title: 'Coffee Ratio Calculator',
    description: 'Calculate coffee and water amounts.',
  },
  {
    slug: 'espresso-shot-timer',
    title: 'Espresso Shot Timer',
    description: 'Time espresso shots and track first drip.',
  },
  {
    slug: 'coffee-price-per-cup-calculator',
    title: coffeeCostGuide.h1,
    description: coffeeCostGuide.description,
  },
  {
    slug: 'cold-brew-calculator',
    title: 'Cold Brew Calculator',
    description: 'Calculate cold brew ratios.',
  },
];

const toolMap = tools.reduce((acc, tool) => {
  acc[tool.slug] = tool;
  return acc;
}, {});

const getSlugFromProps = props => {
  const fromParams = props?.params?.slug;
  if (fromParams) return fromParams;

  if (typeof window === 'undefined') return null;

  const parts = window.location.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  const last = parts[parts.length - 1];

  return last === 'tools' ? null : last;
};


const toolShareCopy = {
  index: {
    path: '/coffee/tools',
    text: 'Free no-login coffee tools for fixing and brewing better coffee.',
  },
  'coffee-taste-fixer': {
    path: '/coffee/tools/coffee-taste-fixer',
    text: 'Free coffee taste fixer for bitter, sour, weak, harsh, or dull coffee.',
  },
  'coffee-ratio-calculator': {
    path: '/coffee/tools/coffee-ratio-calculator',
    text: 'Simple coffee-to-water ratio calculator.',
  },
  'espresso-shot-timer': {
    path: '/coffee/tools/espresso-shot-timer',
    text: 'Free espresso shot timer with dose and yield tracking.',
  },
  'coffee-price-per-cup-calculator': {
    path: '/coffee/tools/coffee-price-per-cup-calculator',
    text: 'Coffee price per cup calculator.',
  },
  'cold-brew-calculator': {
    path: '/coffee/tools/cold-brew-calculator',
    text: 'Simple cold brew ratio calculator.',
  },
};

const ShareToolButtons = ({ slug, title }) => {
  const [copied, setCopied] = useState(false);
  const key = slug || 'index';
  const share = toolShareCopy[key] || toolShareCopy.index;

  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://www.beanpigeon.com';

  const url = `${origin}${share.path}`;
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${share.text} ${url}`)}`;

  const copyLink = async () => {
    const copyText = `${share.text} ${url}`;

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyText);
      } else if (typeof window !== 'undefined') {
        window.prompt('Copy this link', copyText);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      if (typeof window !== 'undefined') {
        window.prompt('Copy this link', copyText);
      }
    }
  };

  const nativeShare = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title,
          text: share.text,
          url,
        });
        return;
      } catch (err) {
        // User cancelled or browser blocked. Fall back to copy.
      }
    }

    copyLink();
  };

  return (
    <section className={css.shareBox} aria-label="Share this Bean Pigeon tool">
      <div>
        <strong>Share this tool</strong>
        <span>{share.text}</span>
      </div>

      <div className={css.shareButtonRow}>
        <button type="button" className={css.copyButton} onClick={copyLink}>
          {copied ? 'Copied' : 'Copy link'}
        </button>
        <button type="button" className={css.copyButton} onClick={nativeShare}>
          Share
        </button>
        <a className={css.shareLink} href={xUrl} target="_blank" rel="noreferrer">
          Post on X
        </a>
      </div>
    </section>
  );
};


const ToolLinks = ({ activeSlug }) => (
  <div className={css.toolLinks}>
    <a className={!activeSlug ? css.activeToolLink : css.toolLink} href="/coffee/tools">
      All tools
    </a>
    {tools.map(tool => (
      <a
        key={tool.slug}
        className={activeSlug === tool.slug ? css.activeToolLink : css.toolLink}
        href={`/coffee/tools/${tool.slug}`}
      >
        {tool.title}
      </a>
    ))}
  </div>
);

const ToolHero = ({ eyebrow = 'Coffee tools', title, description, children }) => (
  <section className={css.hero}>
    <div className={css.eyebrow}>{eyebrow}</div>
    <h1>{title}</h1>
    <p>{description}</p>
    {children ? <div className={css.heroActions}>{children}</div> : null}
  </section>
);

const ToolsIndex = () => (
  <>
    <ToolHero
      title="Coffee Tools"
      description="Simple, mobile-friendly tools for fixing, brewing, and choosing better coffee."
    >
      <a className={css.primaryButton} href="/coffee/tools/coffee-taste-fixer">
        Fix my coffee
      </a>
      <a className={css.secondaryButton} href="/?quiz=1">
        Take the quiz
      </a>
    </ToolHero>

    <ToolLinks activeSlug={null} />

    <ShareToolButtons slug={null} title="Bean Pigeon Coffee Tools" />

    <section className={css.cardGrid}>
      {tools.map(tool => (
        <a key={tool.slug} className={css.toolCard} href={`/coffee/tools/${tool.slug}`}>
          <h2>{tool.title}</h2>
          <p>{tool.description}</p>
          <span>Open tool →</span>
        </a>
      ))}
    </section>
  </>
);

const TasteFixer = () => {
  const [problem, setProblem] = useState('bitter');
  const [method, setMethod] = useState('drip');

  const fixes = {
    bitter: {
      title: 'Your coffee tastes too bitter',
      likely: 'Likely cause: over-extraction, too fine a grind, too much brew time, or very roasty beans.',
      brew: 'Try this: grind a little coarser, shorten brew time, or use slightly less coffee.',
      beans: 'Bean style to try: medium roast with chocolate, caramel, nutty, or brown sugar notes.',
      href: '/coffee/collections/low-acid-coffee-beans',
      cta: 'Browse smoother beans',
    },
    sour: {
      title: 'Your coffee tastes too sour or sharp',
      likely: 'Likely cause: under-extraction, too coarse a grind, water too cool, or very bright beans.',
      brew: 'Try this: grind finer, brew a little longer, or use hotter water.',
      beans: 'Bean style to try: medium roast, chocolatey notes, and lower acidity.',
      href: '/coffee/collections/low-acid-coffee-beans',
      cta: 'Browse low acid beans',
    },
    weak: {
      title: 'Your coffee tastes weak or watery',
      likely: 'Likely cause: too much water, not enough coffee, grind too coarse, or short brew time.',
      brew: 'Try this: use more coffee, less water, or a slightly finer grind.',
      beans: 'Bean style to try: full-bodied medium roast or medium-dark blend.',
      href: '/coffee/collections/medium-roast-coffee-beans',
      cta: 'Browse medium roast beans',
    },
    harsh: {
      title: 'Your coffee tastes harsh or dry',
      likely: 'Likely cause: over-extraction, uneven extraction, too much agitation, or very dark beans.',
      brew: 'Try this: grind coarser, reduce agitation, and avoid letting brewed coffee sit too long.',
      beans: 'Bean style to try: smoother blends with nutty, chocolate, or caramel notes.',
      href: '/coffee/collections/low-acid-coffee-beans',
      cta: 'Browse smoother beans',
    },
    dull: {
      title: 'Your coffee tastes muddy or dull',
      likely: 'Likely cause: stale beans, water not hot enough, old grind, or too much brew time.',
      brew: 'Try this: use fresher beans, grind right before brewing, and check your ratio.',
      beans: 'Bean style to try: fresh medium roast from an independent roaster.',
      href: '/coffee/collections/coffee-beans-under-20',
      cta: 'Browse coffee under $20',
    },
  };

  const methodTip = {
    espresso: 'For espresso, change one variable at a time: grind, dose, yield, or time.',
    'french-press': 'For French press, try a slightly coarser grind and avoid pressing too hard.',
    'cold-brew': 'For cold brew, adjust steep time and dilution before changing beans.',
    'pour-over': 'For pour over, grind size and pour speed can change flavor quickly.',
    drip: 'For drip coffee, start by checking ratio and grind size.',
  }[method];

  const fix = fixes[problem];

  return (
    <section className={css.panel}>
      <h2>What tastes wrong?</h2>

      <div className={css.fieldGrid}>
        <label>
          Problem
          <select value={problem} onChange={e => setProblem(e.target.value)}>
            <option value="bitter">Too bitter</option>
            <option value="sour">Too sour or sharp</option>
            <option value="weak">Weak or watery</option>
            <option value="harsh">Harsh or dry</option>
            <option value="dull">Muddy or dull</option>
          </select>
        </label>

        <label>
          Brew method
          <select value={method} onChange={e => setMethod(e.target.value)}>
            <option value="drip">Drip coffee</option>
            <option value="pour-over">Pour over</option>
            <option value="french-press">French press</option>
            <option value="espresso">Espresso</option>
            <option value="cold-brew">Cold brew</option>
          </select>
        </label>
      </div>

      <div className={css.result}>
        <strong>{fix.title}</strong>
        <p>{fix.likely}</p>
        <p>{fix.brew}</p>
        <p>{fix.beans}</p>
        <p className={css.muted}>{methodTip}</p>
        <a className={css.primaryButton} href={fix.href}>
          {fix.cta}
        </a>
      </div>
    </section>
  );
};

const RatioCalculator = () => {
  const [mode, setMode] = useState('water');
  const [ratio, setRatio] = useState(16);
  const [coffee, setCoffee] = useState(25);
  const [water, setWater] = useState(400);

  const result = useMemo(() => {
    const r = Number(ratio);

    if (mode === 'water') {
      const c = Number(coffee);
      if (!c || !r) return 'Enter coffee grams.';
      const w = Math.round(c * r);
      return `Use ${w}g water for ${c}g coffee.`;
    }

    const w = Number(water);
    if (!w || !r) return 'Enter water grams.';
    const c = Math.round((w / r) * 10) / 10;
    return `Use ${c}g coffee for ${w}g water.`;
  }, [mode, ratio, coffee, water]);

  return (
    <section className={css.panel}>
      <h2>Calculate your coffee ratio</h2>

      <div className={css.fieldGrid}>
        <label>
          Calculate
          <select value={mode} onChange={e => setMode(e.target.value)}>
            <option value="water">Water from coffee</option>
            <option value="coffee">Coffee from water</option>
          </select>
        </label>

        <label>
          Ratio
          <select value={ratio} onChange={e => setRatio(Number(e.target.value))}>
            <option value={14}>1:14 stronger</option>
            <option value={15}>1:15 French press</option>
            <option value={16}>1:16 balanced</option>
            <option value={17}>1:17 lighter</option>
          </select>
        </label>

        <label>
          Coffee grams
          <input
            type="number"
            inputMode="decimal"
            value={coffee}
            onChange={e => setCoffee(e.target.value)}
          />
        </label>

        <label>
          Water grams
          <input
            type="number"
            inputMode="decimal"
            value={water}
            onChange={e => setWater(e.target.value)}
          />
        </label>
      </div>

      <div className={css.result}>
        <strong>{result}</strong>
        <p className={css.muted}>Ratio selected: 1:{ratio}</p>
      </div>
    </section>
  );
};

const EspressoTimer = () => {
  const [running, setRunning] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [firstDripMs, setFirstDripMs] = useState(null);
  const [dose, setDose] = useState(18);
  const [yieldOut, setYieldOut] = useState(36);
  const startAt = useRef(null);
  const frame = useRef(null);

  useEffect(() => {
    if (!running) return undefined;

    const tick = () => {
      setElapsedMs(performance.now() - startAt.current);
      frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame.current);
  }, [running]);

  const fmt = ms => `${(ms / 1000).toFixed(1)}s`;
  const ratio = Number(dose) > 0 && Number(yieldOut) > 0 ? (Number(yieldOut) / Number(dose)).toFixed(2) : '--';

  const start = () => {
    startAt.current = performance.now();
    setElapsedMs(0);
    setFirstDripMs(null);
    setRunning(true);
  };

  const stop = () => {
    if (running && startAt.current) {
      setElapsedMs(performance.now() - startAt.current);
    }
    setRunning(false);
  };

  const reset = () => {
    setRunning(false);
    setElapsedMs(0);
    setFirstDripMs(null);
    startAt.current = null;
  };

  const markFirstDrip = () => {
    if (!running || !startAt.current) return;
    setFirstDripMs(performance.now() - startAt.current);
  };

  return (
    <section className={css.panel}>
      <h2>Time your espresso shot</h2>

      <div className={css.statGrid}>
        <div className={css.stat}>
          <span>Total time</span>
          <strong>{fmt(elapsedMs)}</strong>
        </div>
        <div className={css.stat}>
          <span>First drip</span>
          <strong>{firstDripMs == null ? '--' : fmt(firstDripMs)}</strong>
        </div>
      </div>

      <div className={css.fieldGrid}>
        <label>
          Dose in, grams
          <input
            type="number"
            inputMode="decimal"
            value={dose}
            onChange={e => setDose(e.target.value)}
          />
        </label>

        <label>
          Yield out, grams
          <input
            type="number"
            inputMode="decimal"
            value={yieldOut}
            onChange={e => setYieldOut(e.target.value)}
          />
        </label>
      </div>

      <div className={css.buttonGrid}>
        <button type="button" onClick={start}>
          Start shot
        </button>
        <button type="button" className={css.secondaryAction} onClick={markFirstDrip}>
          Mark first drip
        </button>
        <button type="button" className={css.secondaryAction} onClick={stop}>
          Stop
        </button>
        <button type="button" className={css.secondaryAction} onClick={reset}>
          Reset
        </button>
      </div>

      <div className={css.result}>
        <strong>
          {fmt(elapsedMs)} total, about 1:{ratio}
        </strong>
        <p className={css.muted}>
          A common starting point is 18g in, 36g out, around 25–35 seconds.
        </p>
      </div>
    </section>
  );
};

const CoffeeCostGuide = () => (
  <section className={css.panel} aria-label="Coffee cost guide">
    <h2>{coffeeCostGuide.guide.title}</h2>
    {coffeeCostGuide.guide.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
    <h3>Worked cost examples</h3>
    <ul>{coffeeCostGuide.guide.examples.map(example => <li key={example}>{example}</li>)}</ul>
    <h2>Coffee cost per cup FAQ</h2>
    {coffeeCostGuide.faq.map(([question, answer]) => (
      <details key={question}>
        <summary>{question}</summary>
        <p>{answer}</p>
      </details>
    ))}
    <h3>Compare coffee value</h3>
    <div className={css.inlineLinks}>
      {coffeeCostGuide.relatedLinks.map(link => <a key={link.href} href={link.href}>{link.label}</a>)}
    </div>
  </section>
);

const PricePerCupCalculator = () => {
  const [bagPrice, setBagPrice] = useState(18);
  const [bagSizeOz, setBagSizeOz] = useState(12);
  const [gramsPerCup, setGramsPerCup] = useState(20);
  const [cupsPerDay, setCupsPerDay] = useState(1);

  const result = useMemo(() => {
    const price = Number(bagPrice);
    const oz = Number(bagSizeOz);
    const grams = Number(gramsPerCup);
    const daily = Number(cupsPerDay);

    if (!price || !oz || !grams) {
      return {
        main: 'Enter bag price, bag size, and grams per cup.',
        detail: '',
      };
    }

    const bagGrams = oz * 28.3495;
    const cups = bagGrams / grams;
    const perCup = price / cups;
    const monthly = perCup * daily * 30;

    return {
      main: `About ${cups.toFixed(1)} cups per bag at $${perCup.toFixed(2)} per cup.`,
      detail: `At ${daily} cup(s) per day, that is about $${monthly.toFixed(2)} per month.`,
    };
  }, [bagPrice, bagSizeOz, gramsPerCup, cupsPerDay]);

  return (
    <section className={css.panel}>
      <h2>Calculate price per cup</h2>

      <div className={css.fieldGrid}>
        <label>
          Bag price, dollars
          <input type="number" inputMode="decimal" value={bagPrice} onChange={e => setBagPrice(e.target.value)} />
        </label>
        <label>
          Bag size, oz
          <input type="number" inputMode="decimal" value={bagSizeOz} onChange={e => setBagSizeOz(e.target.value)} />
        </label>
        <label>
          Coffee per cup, grams
          <input type="number" inputMode="decimal" value={gramsPerCup} onChange={e => setGramsPerCup(e.target.value)} />
        </label>
        <label>
          Cups per day
          <input type="number" inputMode="decimal" value={cupsPerDay} onChange={e => setCupsPerDay(e.target.value)} />
        </label>
      </div>

      <div className={css.result}>
        <strong>{result.main}</strong>
        {result.detail ? <p className={css.muted}>{result.detail}</p> : null}
      </div>
    </section>
  );
};

const ColdBrewCalculator = () => {
  const [ratio, setRatio] = useState(12);
  const [target, setTarget] = useState(1000);

  const result = useMemo(() => {
    const r = Number(ratio);
    const total = Number(target);

    if (!r || !total) {
      return 'Enter your target batch size.';
    }

    const coffee = Math.round(total / (r + 1));
    const water = Math.round(total - coffee);

    return `Use about ${coffee}g coffee and ${water}g water.`;
  }, [ratio, target]);

  return (
    <section className={css.panel}>
      <h2>Calculate cold brew</h2>

      <div className={css.fieldGrid}>
        <label>
          Cold brew style
          <select value={ratio} onChange={e => setRatio(Number(e.target.value))}>
            <option value={8}>Concentrate, 1:8</option>
            <option value={12}>Ready to drink, 1:12</option>
            <option value={15}>Lighter ready to drink, 1:15</option>
          </select>
        </label>

        <label>
          Target batch, grams/ml
          <input type="number" inputMode="decimal" value={target} onChange={e => setTarget(e.target.value)} />
        </label>
      </div>

      <div className={css.result}>
        <strong>{result}</strong>
        <p className={css.muted}>Steep cold, usually 12–18 hours, then dilute concentrate to taste.</p>
      </div>
    </section>
  );
};

const renderTool = slug => {
  if (slug === 'coffee-taste-fixer') return <TasteFixer />;
  if (slug === 'coffee-ratio-calculator') return <RatioCalculator />;
  if (slug === 'espresso-shot-timer') return <EspressoTimer />;
  if (slug === 'coffee-price-per-cup-calculator') return <PricePerCupCalculator />;
  if (slug === 'cold-brew-calculator') return <ColdBrewCalculator />;
  return null;
};

const CoffeeToolsPage = props => {
  const slug = getSlugFromProps(props);
  const tool = slug ? toolMap[slug] : null;

  if (slug && !tool) {
    return (
      <Page title="Coffee Tool Not Found | Bean Pigeon" description="We could not find that Bean Pigeon coffee tool.">
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <main className={css.page}>
            <ToolHero title="Coffee tool not found" description="That tool does not exist yet. Try one of these instead." />
            <ToolLinks activeSlug={null} />
          </main>
        </LayoutSingleColumn>
      </Page>
    );
  }

  if (!slug) {
    return (
      <Page title="Coffee Tools | Bean Pigeon" description="Free mobile-friendly coffee tools from Bean Pigeon.">
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <main className={css.page}>
            <ToolsIndex />
          </main>
        </LayoutSingleColumn>
      </Page>
    );
  }

  return (
    <Page title={`${tool.title} | Bean Pigeon`} description={tool.description}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.page}>
          <ToolHero title={tool.title} description={tool.description}>
            <a className={css.primaryButton} href="/?quiz=1">
              Find beans for me
            </a>
            <a className={css.secondaryButton} href="/coffee/tools">
              All tools
            </a>
          </ToolHero>

          <ToolLinks activeSlug={slug} />

          <ShareToolButtons slug={slug} title={tool.title} />

          <div className={css.content}>{renderTool(slug)}</div>
          {slug === 'coffee-price-per-cup-calculator' ? <CoffeeCostGuide /> : null}

          <section className={css.panel}>
            <h2>Helpful next steps</h2>
            <p>Use the tools to brew better, then use Bean Pigeon to find beans that match your taste.</p>
            <div className={css.inlineLinks}>
              <a href="/coffee/collections/coffee-beans-under-20">Coffee under $20</a>
              <a href="/coffee/collections/low-acid-coffee-beans">Low acid coffee</a>
              <a href="/coffee/collections/espresso-coffee-beans">Espresso beans</a>
              <a href="/?quiz=1">Take the quiz</a>
            </div>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CoffeeToolsPage;

